import type { ButtonVariant } from '@heidi/design-system/button';

import {
  CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS,
  CLINICIAN_MAX_UPGRADE_BANNER_CLASS,
  CLINICIAN_MAX_UPGRADE_BANNER_INK_CLASS,
} from '../clinician-max-upgrade-theme';
import { creditLimitReachedUpgradeMessages } from './credit-limit-reached-upgrade-messages';
import type {
  CreditAllowanceNudgeThreshold,
  CreditLimitReachedUpgradePlan,
  CreditLimitReachedUpgradePreset,
} from './types';

const clinicianMaxUpgradeTheme = {
  bannerSubVariant: 'default',
  bannerClassName: CLINICIAN_MAX_UPGRADE_BANNER_CLASS,
  bannerIconClass: CLINICIAN_MAX_UPGRADE_BANNER_INK_CLASS,
  bannerTitleClass: CLINICIAN_MAX_UPGRADE_BANNER_INK_CLASS,
  bannerDescriptionClass: 'text-text-secondary',
  buttonVariant: 'secondary',
  buttonClassName: CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS,
} as const satisfies {
  bannerSubVariant: CreditLimitReachedUpgradePreset['theme']['bannerSubVariant'];
  bannerClassName: string;
  bannerIconClass: string;
  bannerTitleClass: string;
  bannerDescriptionClass: string;
  buttonVariant: ButtonVariant;
  buttonClassName: string;
};

const usageThresholdBase = {
  clinicianMax: {
    theme: clinicianMaxUpgradeTheme,
    messages: {
      description: creditLimitReachedUpgradeMessages.clinicianMaxHalfUsageDescription,
      ctaLabel: creditLimitReachedUpgradeMessages.clinicianMaxUpgradeCta,
      ctaPlanName: creditLimitReachedUpgradeMessages.clinicianMaxPlanName,
    },
    dismissible: true,
  },
  practiceMax: {
    theme: {
      bannerSubVariant: 'practice',
      buttonVariant: 'pro_plus',
    },
    messages: {
      description: creditLimitReachedUpgradeMessages.practiceMaxHalfUsageDescription,
      ctaLabel: creditLimitReachedUpgradeMessages.practiceMaxUpgradeCta,
      ctaPlanName: creditLimitReachedUpgradeMessages.practiceMaxPlanName,
    },
    dismissible: true,
  },
} as const satisfies Record<
  CreditLimitReachedUpgradePlan,
  Pick<CreditLimitReachedUpgradePreset, 'theme' | 'dismissible'> & {
    messages: Omit<CreditLimitReachedUpgradePreset['messages'], 'title'>;
  }
>;

const exhaustedPreset = {
  clinicianMax: {
    theme: usageThresholdBase.clinicianMax.theme,
    messages: {
      title: creditLimitReachedUpgradeMessages.clinicianMaxExhaustedTitle,
      description: creditLimitReachedUpgradeMessages.clinicianMaxExhaustedDescription,
      ctaLabel: creditLimitReachedUpgradeMessages.clinicianMaxUpgradeCta,
      ctaPlanName: creditLimitReachedUpgradeMessages.clinicianMaxPlanName,
    },
    dismissible: false,
  },
  practiceMax: {
    theme: usageThresholdBase.practiceMax.theme,
    messages: {
      title: creditLimitReachedUpgradeMessages.practiceMaxExhaustedTitle,
      description: creditLimitReachedUpgradeMessages.practiceMaxExhaustedDescription,
      ctaLabel: creditLimitReachedUpgradeMessages.practiceMaxUpgradeCta,
      ctaPlanName: creditLimitReachedUpgradeMessages.practiceMaxPlanName,
    },
    dismissible: false,
  },
} as const satisfies Record<
  CreditLimitReachedUpgradePlan,
  CreditLimitReachedUpgradePreset
>;

/** Preset for an allowance nudge rung — 50/75/90 share threshold title; 100 is exhausted. */
export function getCreditLimitReachedUpgradePreset(
  plan: CreditLimitReachedUpgradePlan,
  threshold: CreditAllowanceNudgeThreshold
): CreditLimitReachedUpgradePreset {
  if (threshold === 100) {
    return exhaustedPreset[plan];
  }

  const base = usageThresholdBase[plan];

  return {
    theme: base.theme,
    dismissible: base.dismissible,
    messages: {
      title:
        plan === 'practiceMax'
          ? creditLimitReachedUpgradeMessages.practiceMaxUsageThresholdTitle
          : creditLimitReachedUpgradeMessages.clinicianMaxUsageThresholdTitle,
      description: base.messages.description,
      ctaLabel: base.messages.ctaLabel,
      ctaPlanName: base.messages.ctaPlanName,
    },
  };
}
